import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';

export type AppThemeMode = 'light' | 'dark';

const typography: ThemeOptions['typography'] = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
  h4: {
    fontWeight: 600,
    letterSpacing: '-0.02em',
  },
  h5: {
    fontWeight: 600,
    letterSpacing: '-0.01em',
  },
  h6: {
    fontWeight: 600,
  },
  button: {
    textTransform: 'none',
    fontWeight: 500,
  },
};

const shadows: Theme['shadows'] = [
  'none',
  '0px 2px 4px rgba(0,0,0,0.05)',
  '0px 4px 8px rgba(0,0,0,0.08)',
  '0px 8px 16px rgba(0,0,0,0.1)',
  '0px 12px 24px rgba(0,0,0,0.12)',
  '0px 16px 32px rgba(0,0,0,0.14)',
  '0px 20px 40px rgba(0,0,0,0.16)',
  '0px 24px 48px rgba(0,0,0,0.18)',
  '0px 28px 56px rgba(0,0,0,0.2)',
  '0px 32px 64px rgba(0,0,0,0.22)',
  '0px 36px 72px rgba(0,0,0,0.24)',
  '0px 40px 80px rgba(0,0,0,0.26)',
  '0px 44px 88px rgba(0,0,0,0.28)',
  '0px 48px 96px rgba(0,0,0,0.3)',
  '0px 52px 104px rgba(0,0,0,0.32)',
  '0px 56px 112px rgba(0,0,0,0.34)',
  '0px 60px 120px rgba(0,0,0,0.36)',
  '0px 64px 128px rgba(0,0,0,0.38)',
  '0px 68px 136px rgba(0,0,0,0.4)',
  '0px 72px 144px rgba(0,0,0,0.42)',
  '0px 76px 152px rgba(0,0,0,0.44)',
  '0px 80px 160px rgba(0,0,0,0.46)',
  '0px 84px 168px rgba(0,0,0,0.48)',
  '0px 88px 176px rgba(0,0,0,0.5)',
  '0px 92px 184px rgba(0,0,0,0.52)',
];

export const getAppTheme = (mode: AppThemeMode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: '#F44336',
        light: '#FF7A6E',
        dark: '#B71C1C',
        contrastText: '#fff',
      },
      secondary: {
        main: '#1565C0',
        light: '#5E92F3',
        dark: '#0D47A1',
        contrastText: '#fff',
      },
      background:
        mode === 'dark'
          ? { default: '#121212', paper: '#1e1e1e' }
          : { default: '#f5f5f5', paper: '#ffffff' },
      text:
        mode === 'dark'
          ? { primary: 'rgba(255, 255, 255, 0.87)', secondary: 'rgba(255, 255, 255, 0.6)' }
          : { primary: 'rgba(0, 0, 0, 0.87)', secondary: 'rgba(0, 0, 0, 0.6)' },
    },
    typography,
    shape: {
      borderRadius: 8,
    },
    shadows,
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '8px 16px',
          },
          contained: {
            boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
            '&:hover': {
              boxShadow: '0px 4px 8px rgba(0,0,0,0.15)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
          },
          elevation1: {
            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
          },
          elevation2: {
            boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0px 2px 8px rgba(0,0,0,0.1)',
            borderRadius: 0,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            backgroundColor: mode === 'dark' ? '#2a2a2a' : '#fafafa',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
    },
  });
